// COMPONENTS & FUNCTIONS
import {
  equationHandler,
  convertHandler,
  calculateHandler,
} from "./mathHandler";

// DATA
import * as calculators from "../engineering/calculators";

// NPM INSTALLS
import { v4 as UUID } from "uuid";

// INTERFACES
import {
  AppData,
  BaseQuantity,
} from "../interface/functionalityInterface";
import {
  CalculatorUnitProps,
  ConverterUnitProps,
  ReceiptUnitProps,
} from "../interface/propTypesInterface";

export const createCalculator = (calcName: string) => {
  const listofCalcs = Object.entries(calculators).map((entry) => entry[1]);
  const index = listofCalcs.findIndex((i) => i.name === calcName);
  if (index === -1) throw new Error(`no calculator name: ${calcName}`);
  const calcSelected = { ...listofCalcs[index] };
  const calculator: CalculatorUnitProps | ConverterUnitProps = {
    ...calcSelected,
    id: UUID(),
    selectedCalcMode: 0,
    noDecimals: 1,
    enableOperators: true,
    priorityList: [],
    inputs: calcSelected.inputs.map((mode) => {
      return {
        ...mode,
        id: UUID(),
        baseQuantities: mode.baseQuantities.map((bq: BaseQuantity) => {
          return {
            ...bq,
            id: UUID(),
          };
        }),
      };
    }),
    removeUnit: () => {},
    duplicateUnit: () => {},
    addToReceipt: () => {},
    dragDrop: () => {},
  };
  // Give ids after they have been given to BaseQuantitites
  calculator.priorityList = calculator.inputs[0].baseQuantities
    // .map((bq) => bq)
    // .sort((a, b) => (a.value === "" ? 1 : b.value === "" ? -1 : 0))
    .filter((bq) => bq.type !== "hidden")
    .map((bq) => bq.id);

  return calculator;
};

export const getCalculatorsFromLocalStorage = () => {
  const calculators: (CalculatorUnitProps | ConverterUnitProps)[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key === null) return;
    const calcJSON = localStorage.getItem(key);
    if (calcJSON === null) return;
    const calculator = JSON.parse(calcJSON) as
      | CalculatorUnitProps
      | ConverterUnitProps;

    if (
      calculator.type === AppData.CALCULATOR ||
      calculator.type === AppData.CONVERTER
    ) {
      calculators.push(calculator);
    }
  }
  return calculators;
};

export const duplicateCalculator = (
  calc: CalculatorUnitProps | ConverterUnitProps
) => {
  const calculator: CalculatorUnitProps | ConverterUnitProps = {
    ...calc,
    id: UUID(),
    priorityList: [],
    graphConfiguration: calc.graphConfiguration
      ? {
          ...calc.graphConfiguration,
          legends: calc.graphConfiguration.legends.map((legend) => ({
            ...legend,
            id: UUID(),
          })),
          savedPoints: (calc.graphConfiguration.savedPoints ?? []).map(
            (point) => ({
              ...point,
              id: UUID(),
              values: point.values.map((value) => ({ ...value })),
            })
          ),
        }
      : undefined,
    inputs: calc.inputs.map((mode) => {
      return {
        ...mode,
        id: UUID(),
        baseQuantities: mode.baseQuantities.map((bq) => {
          return {
            ...bq,
            id: UUID(),
          };
        }),
      };
    }),
  };
  // Give ids after they have been given to BaseQuantitites
  calculator.priorityList = calculator.inputs[
    calculator.selectedCalcMode
  ].baseQuantities.map((bq) => bq.id);
  // addAppDataToLocalStorage(calculator);
  return calculator;
};

export const checkInput = (inputVal: string, hasOperators: boolean) => {
  const checkInput = inputVal.match(
    hasOperators
      ? /((^|(\+|\-|\*|\/|\^|\.|\(|\)))([0-9]*))+$/
      : /((^|(\.))([0-9]*))+$/
  );

  return checkInput ? true : false;
};

export const checkTitle = (inputVal: string) => {
  const checkInput = inputVal.match(
    // /((^|(\+|\-|\*|\/|\^|\.|\(|\)))([0-9]*))+$/
    /^$|^[a-z A-Z\å\ä\ö\Å\Ä\Ö\é\É\ü\Ü\.\ ]+$/
  );

  return checkInput ? true : false;
};

export const updatePriorityList = (
  calc: CalculatorUnitProps | ConverterUnitProps,
  targetID: string
): CalculatorUnitProps | ConverterUnitProps => {
  return {
    ...calc,
    priorityList: calc.priorityList.sort((a, b) => {
      if (a === targetID) return 1;
      if (b === targetID) return -1;
      else return 0;
    }),
  };
};

export const lastInPriorityList = (
  calc: CalculatorUnitProps,
  targetID: string
): CalculatorUnitProps => {
  return {
    ...calc,
    priorityList: calc.priorityList.sort((a, b) => {
      if (a === targetID) return -1;
      if (b === targetID) return 1;
      else return 0;
    }),
  };
};

export const updateInputField = (
  calc: CalculatorUnitProps | ConverterUnitProps,
  targetID: string,
  targetValue: string
): CalculatorUnitProps | ConverterUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcMode) => {
      return {
        ...calcMode,
        baseQuantities: calcMode.baseQuantities.map((bq) => {
          return {
            ...bq,
            value: bq.id === targetID ? targetValue : bq.value,
          };
        }),
      };
    }),
  };
};

// NOTE that prioritylist needs to be updated as well in order for this to work
export const addInputField = (
  calc: CalculatorUnitProps | ConverterUnitProps
): CalculatorUnitProps | ConverterUnitProps => {
  const baseQuantities = calc.inputs[calc.selectedCalcMode].baseQuantities;
  const newBaseQuantity: BaseQuantity = {
    ...baseQuantities[baseQuantities.length - 1],
    id: UUID(),
  };
  const extendedBaseQuantities = [
    ...calc.inputs[calc.selectedCalcMode].baseQuantities.map((bq) => bq),
    newBaseQuantity,
  ];

  return {
    ...calc,
    priorityList: extendedBaseQuantities.map((bq) => bq.id),
    inputs: calc.inputs.map((calcMode, calcModeIndex) => {
      return {
        ...calcMode,
        baseQuantities:
          calc.selectedCalcMode === calcModeIndex
            ? extendedBaseQuantities
            : calcMode.baseQuantities.map((bq) => {
                return {
                  ...bq,
                };
              }),
      };
    }),
  };
};

export const checkEmptyInputs = (
  calc: CalculatorUnitProps | ConverterUnitProps
) => {
  const emptyInputs = calc.inputs[calc.selectedCalcMode].baseQuantities.reduce(
    (acc, nV) => (nV.value === "" ? (acc += 1) : acc),
    0
  );
  return emptyInputs;
};

export const calculateInputField = (
  calc: CalculatorUnitProps
): CalculatorUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcMode) => {
      return {
        ...calcMode,
        baseQuantities: calcMode.baseQuantities.map((bq) => {
          return {
            ...bq,
            value:
              bq.id === calc.priorityList[0]
                ? String(
                    Number(equationHandler(bq, calc)).toFixed(calc.noDecimals)
                  )
                : bq.value,
          };
        }),
      };
    }),
  };
};

export const convertAllUnits = (
  calc: ConverterUnitProps,
  bq: BaseQuantity
): ConverterUnitProps => {
  const fromUnit = bq.selectedUnit;
  const fromValue = bq.value;
  return {
    ...calc,
    inputs: calc.inputs.map((calcMode, index) => {
      if (calc.selectedCalcMode !== index) return { ...calcMode };
      else
        return {
          ...calcMode,
          baseQuantities: calcMode.baseQuantities.map((bq) => {
            return {
              ...bq,
              value:
                fromValue === "" // if the from value is an empty string then all should be empty
                  ? ""
                  : bq.id === calc.priorityList[calc.priorityList.length - 1] ||
                    calculateHandler(fromValue) === null // convert all units of all fields but the last inputted field. Also if error in parsing because of incomplete input, then allow null to pass
                  ? bq.value
                  : convertHandler(
                      calculateHandler(fromValue)!.toString(),
                      bq.selectedUnit,
                      fromUnit,
                      bq.units
                    ).toFixed(calc.noDecimals),
            };
          }),
        };
    }),
  };
};

export const updateConvertDecimals = (
  calc: ConverterUnitProps
): ConverterUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcMode, index) => {
      if (calc.selectedCalcMode !== index) return { ...calcMode };
      else
        return {
          ...calcMode,
          baseQuantities: calcMode.baseQuantities.map((bq) => {
            return {
              ...bq,
              value: Number(bq.value).toFixed(calc.noDecimals),
            };
          }),
        };
    }),
  };
};

export const convertUnit = (
  calc: CalculatorUnitProps | ConverterUnitProps,
  formInput: BaseQuantity,
  selectedUnit: string
): CalculatorUnitProps | ConverterUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcMode) => {
      return {
        ...calcMode,
        baseQuantities: calcMode.baseQuantities.map((bq) => {
          return {
            ...bq,
            value:
              bq.id === formInput.id && bq.value !== ""
                ? convertHandler(
                    bq.value,
                    selectedUnit,
                    bq.selectedUnit,
                    bq.units
                  ).toFixed(calc.noDecimals)
                : bq.value,
            selectedUnit:
              bq.id === formInput.id ? selectedUnit : bq.selectedUnit,
          };
        }),
      };
    }),
  };
};

export const populateReceipt = (
  unit: CalculatorUnitProps | ConverterUnitProps
): ReceiptUnitProps | null => {
  if (
    unit.inputs[unit.selectedCalcMode].baseQuantities.every(
      (input) => input.value === ""
    )
  )
    return null;
  return {
    id: UUID(),
    type: AppData.RECEIPT,
    label: unit.name,
    output: unit.inputs[unit.selectedCalcMode].baseQuantities.map(
      (input) => {
        return {
          name: input.name,
          value: input.value,
          unit: input.selectedUnit,
        };
      }
    ),
    timestamp: Date.now() / 1000,
    mode: unit.inputs[unit.selectedCalcMode].name,
    deleteCallback: () => {},
  };
};

export const clearCalc = (
  calc: CalculatorUnitProps | ConverterUnitProps
): CalculatorUnitProps | ConverterUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcmode) => {
      return {
        ...calcmode,
        baseQuantities: calcmode.baseQuantities.map((bq) => {
          return {
            ...bq,
            value: "",
          };
        }),
      };
    }),
  };
};

export const updateDecimals = (
  calc: CalculatorUnitProps | ConverterUnitProps
): CalculatorUnitProps | ConverterUnitProps => {
  return {
    ...calc,
    inputs: calc.inputs.map((calcmode) => {
      return {
        ...calcmode,
        baseQuantities: calcmode.baseQuantities.map((bq) => {
          return {
            ...bq,
            value: Number(String(bq.value)).toFixed(calc.noDecimals),
          };
        }),
      };
    }),
  };
};
